import axios from 'axios';
import { supabase } from './supabaseClient';

const API_URL = import.meta.env.VITE_API_URL;

type KamisRow = {
  commodity: string | null;
  classification: string | null;
  county: string | null;
  wholesale_price: number | null;
  retail_price: number | null;
  price_date: string;
};

export interface PricePoint {
  month: string;
  price: number;
}

export interface PricePrediction {
  cropName: string;
  currentPrice: number;
  predictedPrices: PricePoint[];
  confidenceLevel: number;
  factors: {
    factor: string;
    impact: 'positive' | 'negative' | 'neutral';
    description: string;
  }[];
  recommendations: string[];
  lastUpdated: Date;
}

const normalizeTerm = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const titleCase = (value: string) =>
  value
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const pickObservedPrice = (row: KamisRow) => row.retail_price ?? row.wholesale_price ?? null;

const monthLabel = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const average = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const computeHistoricalSeries = (rows: KamisRow[], maxPoints: number = 6): PricePoint[] => {
  const grouped = new Map<string, number[]>();

  rows.forEach((row) => {
    const price = pickObservedPrice(row);
    if (price == null) return;
    const key = row.price_date;
    const bucket = grouped.get(key) ?? [];
    bucket.push(price);
    grouped.set(key, bucket);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-maxPoints)
    .map(([date, values]) => ({
      month: new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      price: Number(average(values).toFixed(2)),
    }));
};

const projectForward = (latestPrice: number, trendRate: number): PricePoint[] => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() + index + 1, 1);
    const drift = clamp(trendRate * (index + 1), -0.18, 0.18);
    const seasonalBias = Math.sin((date.getMonth() / 12) * Math.PI * 2) * 0.015;
    const projected = latestPrice * (1 + drift + seasonalBias);
    return {
      month: monthLabel(date),
      price: Number(Math.max(projected, latestPrice * 0.55).toFixed(2)),
    };
  });
};

const buildFallbackPrediction = (cropName: string, countyName: string | undefined, rows: KamisRow[]): PricePrediction => {
  const pricedRows = rows.filter((row) => pickObservedPrice(row) != null);
  if (!pricedRows.length) {
    throw new Error(
      countyName
        ? `No recent official market records were found for ${cropName} in ${countyName}. Try another county or remove the county filter.`
        : `No recent official market records were found for ${cropName}. Try another crop or upload newer KAMIS data.`,
    );
  }

  const latestDate = pricedRows[0].price_date;
  const latestRows = pricedRows.filter((row) => row.price_date === latestDate);
  const latestPrices = latestRows.map((row) => pickObservedPrice(row)!).filter((value) => Number.isFinite(value));
  const currentPrice = Number(average(latestPrices).toFixed(2));

  const recentWindow = pricedRows.slice(0, Math.min(10, pricedRows.length));
  const olderWindow = pricedRows.slice(Math.min(10, pricedRows.length), Math.min(25, pricedRows.length));
  const recentAverage = average(recentWindow.map((row) => pickObservedPrice(row)!).filter((value) => Number.isFinite(value)));
  const olderAverage = average(olderWindow.map((row) => pickObservedPrice(row)!).filter((value) => Number.isFinite(value)));
  const trendRate = olderAverage > 0 ? clamp((recentAverage - olderAverage) / olderAverage, -0.08, 0.08) : 0;
  const confidenceLevel = clamp(0.45 + pricedRows.length * 0.015 + (countyName ? 0.05 : 0), 0.5, 0.88);
  const historicalSeries = computeHistoricalSeries(pricedRows, 6);
  const predictedPrices = projectForward(currentPrice || recentAverage, trendRate);
  const spreadRows = pricedRows.filter((row) => row.retail_price != null && row.wholesale_price != null);
  const avgSpread = average(
    spreadRows.map((row) => (row.retail_price as number) - (row.wholesale_price as number)).filter((value) => Number.isFinite(value)),
  );

  const factors: PricePrediction['factors'] = [
    {
      factor: 'Recent official market trend',
      impact: trendRate > 0.02 ? 'positive' : trendRate < -0.02 ? 'negative' : 'neutral',
      description:
        trendRate > 0.02
          ? 'Recent official prices are trending upward, which may support stronger selling conditions in the next few months.'
          : trendRate < -0.02
            ? 'Recent official prices are softening, so margins may be tighter unless quality or timing improves.'
            : 'Recent official prices are relatively stable, suggesting moderate short-term volatility.',
    },
    {
      factor: 'County-specific market coverage',
      impact: countyName ? 'positive' : 'neutral',
      description: countyName
        ? `Forecast uses recent official rows from ${countyName}, making it more relevant for local decision-making.`
        : 'Forecast uses the latest official records across available markets, which gives a broader national signal.',
    },
    {
      factor: 'Observed market spread',
      impact: avgSpread > 8 ? 'positive' : avgSpread > 0 ? 'neutral' : 'neutral',
      description:
        avgSpread > 8
          ? 'Retail and wholesale prices show a meaningful spread, which may create room for better margins depending on the sales channel.'
          : 'Retail and wholesale prices are relatively close, which suggests tighter margins across channels.',
    },
  ];

  const recommendations = [
    trendRate > 0.02
      ? 'Consider staggering sales to capture potential upside rather than selling all produce at once.'
      : trendRate < -0.02
        ? 'Prioritize quality grading, storage discipline, and buyer diversification to protect margins.'
        : 'Use a balanced sales plan and monitor weekly official prices before making bulk sales decisions.',
    countyName
      ? `Compare ${countyName} pricing with nearby counties to identify stronger selling markets before harvest or dispatch.`
      : 'Add a county filter to get a more location-specific forecast for procurement or selling decisions.',
    'Review both wholesale and retail channels, since the observed spread can materially affect realized income.',
  ];

  return {
    cropName: titleCase(cropName),
    currentPrice,
    predictedPrices,
    confidenceLevel,
    factors,
    recommendations,
    lastUpdated: new Date(latestDate),
  };
};

const fetchKamisRows = async (cropName: string, countyName?: string): Promise<KamisRow[]> => {
  const normalizedCrop = normalizeTerm(cropName);
  const cropWords = normalizedCrop.split(' ').filter((word) => word.length >= 3);
  const searchTerm = cropWords[0] ?? normalizedCrop;

  let query = supabase
    .from('kamis_market_prices')
    .select('commodity, classification, county, wholesale_price, retail_price, price_date')
    .order('price_date', { ascending: false })
    .limit(120);

  if (countyName?.trim()) {
    query = query.ilike('county', `%${countyName.trim()}%`);
  }

  if (searchTerm) {
    query = query.or(`commodity.ilike.%${searchTerm}%,classification.ilike.%${searchTerm}%`);
  }

  const { data, error } = await query;
  if (error) throw error;

  const scored = (data ?? [])
    .filter((row) => {
      const haystack = normalizeTerm(`${row.commodity ?? ''} ${row.classification ?? ''}`);
      return cropWords.every((word) => haystack.includes(word)) || haystack.includes(normalizedCrop) || haystack.includes(searchTerm);
    })
    .sort((a, b) => b.price_date.localeCompare(a.price_date));

  return scored;
};

export const marketPredictionService = {
  async getPrediction(cropName: string, countyName?: string): Promise<PricePrediction> {
    if (API_URL) {
      try {
        const url = countyName
          ? `${API_URL}/market-predictions/${cropName}?county=${countyName}`
          : `${API_URL}/market-predictions/${cropName}`;

        const response = await axios.get(url);
        return response.data;
      } catch (error) {
        console.warn(`Remote market prediction backend failed for ${cropName}, falling back to official market data.`, error);
      }
    }

    const rows = await fetchKamisRows(cropName, countyName);
    return buildFallbackPrediction(cropName, countyName, rows);
  },

  async getHistoricalPrices(cropName: string, months: number = 12): Promise<PricePoint[]> {
    if (API_URL) {
      try {
        const response = await axios.get(`${API_URL}/market-predictions/historical/${cropName}?months=${months}`);
        return response.data;
      } catch (error) {
        console.warn(`Remote historical market data failed for ${cropName}, falling back to official market records.`, error);
      }
    }

    const rows = await fetchKamisRows(cropName);
    const points = computeHistoricalSeries(rows, Math.max(3, Math.min(months, 12)));
    if (!points.length) {
      throw new Error(`No recent official historical prices were found for ${cropName}.`);
    }
    return points;
  }
};
